import { createAsyncThunk } from '@reduxjs/toolkit';
import { AdminRepository } from 'repositories/AdminRepository';
import { IAdminDeviceGroupsState } from 'store/slices/adminDeviceGroupsSlice';
import { IAuthState } from 'store/slices/authSlice';
import { IAdminDeviceGroup, TAdminDeviceGroupsQuery } from 'types';
import { EApiResponseStatus, IApiResponseDetails } from 'types/api';

export const fetchDeviceGroups = createAsyncThunk<
  {
    content: IAdminDeviceGroup[];
    details: IApiResponseDetails;
    sums: {
      normalHashrate: number;
      yesterdayHashrate: number;
      payment: number;
    };
  },
  void,
  { state: { auth: IAuthState; adminDeviceGroups: IAdminDeviceGroupsState } }
>('adminDeviceGroups/fetchDeviceGroups', async (_, { getState }) => {
  const {
    auth: { token },
    adminDeviceGroups: { currentPage, itemsPerPage },
  } = getState();
  if (!token) throw Error('no token');

  const params: TAdminDeviceGroupsQuery = {
    page: String(currentPage),
    size: String(itemsPerPage),
  };

  const adminDeviceGroupsResponse = await AdminRepository.getDeviceGroups(token, params);

  if (adminDeviceGroupsResponse.status === EApiResponseStatus.FAILURE) {
    throw Error(adminDeviceGroupsResponse.error);
  }

  const { content, details } = adminDeviceGroupsResponse.data;

  const sums = content.reduce(
    (sum, group) => ({
      normalHashrate: sum.normalHashrate + Number(group.normalHashrate),
      yesterdayHashrate: sum.yesterdayHashrate + Number(group.yesterdayHashrate),
      payment: sum.payment + Number(group.payment),
    }),
    { normalHashrate: 0, yesterdayHashrate: 0, payment: 0 },
  );

  return { content, details, sums };
});

export const fetchExportDeviceGroups = createAsyncThunk<
  { exportData: string },
  void,
  { state: { auth: IAuthState } }
>('adminDeviceGroups/fetchExportDeviceGroups', async (_, { getState }) => {
  const {
    auth: { token },
  } = getState();

  if (!token) throw Error('no token');

  const adminExportDeviceGroupsResponse = await AdminRepository.getDeviceGroupsExport(token);

  if (adminExportDeviceGroupsResponse.status === EApiResponseStatus.FAILURE) {
    throw Error(adminExportDeviceGroupsResponse.error);
  }

  return {
    exportData: adminExportDeviceGroupsResponse.data,
  };
});
